/**
 * PropertyMapView.jsx — Property/indoor scope for JourneyQueue Map Monitor.
 *
 * Shows sessions with scope=property (walking, prm_vehicle) as SVG markers.
 * Renders a simplified floor-plan of JFK T5 anchor nodes.
 * Also shows deployed AR Node sensePoints as ⬡ hexagonal markers with
 * drag-to-place for nodes that don't yet have a confirmed lat/lng.
 *
 * AR Node display fixes (v373):
 *   1. Position jitter — nodes sharing the same default coordinates are spread
 *      in a small circle so they never stack on top of each other.
 *   2. Listener deduplication — dragend is tracked per node id in dragListenedRef
 *      so ActionCable-driven re-renders never attach a second listener.
 *   3. Admin PATCH route — position saves go to PATCH /admin/ar_nodes/:id/position
 *      (cookie/CSRF auth) instead of the Bearer-token API endpoint.
 */

import { useEffect, useRef, useCallback } from "react";
import { JourneyQueue } from "../../../utils/JourneyQueue.js";
import { PROPERTY_REGISTRY, inPropertyBbox } from "../../../utils/PropertyRegistry.js";

const TILE_URL  = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTR = '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

// ── Phase D: Las Vegas venue floor plans ──────────────────────────────────────
// Approximate walking-path anchor nodes for MGM Grand, VEX, and LVCC.
// Coordinates are geographic (real lat/lng) — they appear as Leaflet overlays
// wherever the map is panned, and only become visible after flyTo moves the
// camera to the respective property.

// ─ MGM Grand ──────────────────────────────────────────────────────────────────
const MGM_ANCHORS = [
  { key: "PORTE_COCHERE",    lat: 36.1018, lng: -115.1680, label: "Porte Cochère",   tooltipDir: "left"  },
  { key: "LOBBY",            lat: 36.1022, lng: -115.1676, label: "Lobby",           tooltipDir: "left"  },
  { key: "CASINO_FLOOR",     lat: 36.1026, lng: -115.1672, label: "Casino Floor",    tooltipDir: "right" },
  { key: "ELEVATORS",        lat: 36.1028, lng: -115.1666, label: "Elevators",       tooltipDir: "right" },
  { key: "POOL_TERRACE",     lat: 36.1032, lng: -115.1663, label: "Pool Terrace",    tooltipDir: "right" },
  { key: "CONFERENCE_WING",  lat: 36.1026, lng: -115.1661, label: "Conference Wing", tooltipDir: "right" },
  { key: "GRAND_GARDEN",     lat: 36.1022, lng: -115.1658, label: "Grand Garden Arena", tooltipDir: "right" },
];

const MGM_OUTLINE = [
  [36.1016, -115.1686],
  [36.1016, -115.1655],
  [36.1035, -115.1655],
  [36.1035, -115.1686],
];

const MGM_ZONES = [
  { key: "ENTRY",      color: "#10B981",
    poly: [[36.1016,-115.1686],[36.1016,-115.1674],[36.1026,-115.1674],[36.1026,-115.1686]] },
  { key: "GAMING",     color: "#F59E0B",
    poly: [[36.1016,-115.1674],[36.1016,-115.1663],[36.1035,-115.1663],[36.1035,-115.1674]] },
  { key: "AMENITIES",  color: "#3B82F6",
    poly: [[36.1026,-115.1663],[36.1026,-115.1655],[36.1035,-115.1655],[36.1035,-115.1663]] },
  { key: "EVENTS",     color: "#7C3AED",
    poly: [[36.1016,-115.1663],[36.1016,-115.1655],[36.1026,-115.1655],[36.1026,-115.1663]] },
];

const MGM_ANCHOR_PROFILES = {
  PORTE_COCHERE:   { capacity: 50, occupied: 18, friction: 0.36, irop: "low",    wait: 3,  services: "Valet · Taxi · Rideshare",         congestion: "#10B981" },
  LOBBY:           { capacity: 80, occupied: 34, friction: 0.43, irop: "low",    wait: 4,  services: "Check-in · Concierge · Bellhop",   congestion: "#10B981" },
  CASINO_FLOOR:    { capacity: 200,occupied: 150,friction: 0.75, irop: "medium", wait: 0,  services: "Table Games · Slots · Keno",       congestion: "#F59E0B" },
  ELEVATORS:       { capacity: 12, occupied: 7,  friction: 0.58, irop: "medium", wait: 6,  services: "Hotel Towers · Penthouse Access",   congestion: "#F59E0B" },
  POOL_TERRACE:    { capacity: 120,occupied: 45, friction: 0.38, irop: "low",    wait: 0,  services: "Pool · Bar · Cabanas",              congestion: "#10B981" },
  CONFERENCE_WING: { capacity: 60, occupied: 28, friction: 0.47, irop: "low",    wait: 2,  services: "Meeting Rooms · Business Center",  congestion: "#10B981" },
  GRAND_GARDEN:    { capacity: 800,occupied: 600,friction: 0.82, irop: "high",   wait: 15, services: "Grand Garden Arena · Live Events",  congestion: "#EF4444" },
};

// ─ Venetian Expo (VEX) ────────────────────────────────────────────────────────
const VEX_ANCHORS = [
  { key: "MAIN_ENTRANCE", lat: 36.1252, lng: -115.1720, label: "Main Entrance", tooltipDir: "left"  },
  { key: "HALL_A",        lat: 36.1252, lng: -115.1710, label: "Hall A",        tooltipDir: "left"  },
  { key: "HALL_B",        lat: 36.1252, lng: -115.1702, label: "Hall B",        tooltipDir: "top"   },
  { key: "HALL_C",        lat: 36.1252, lng: -115.1694, label: "Hall C",        tooltipDir: "right" },
  { key: "HALL_D",        lat: 36.1252, lng: -115.1686, label: "Hall D",        tooltipDir: "right" },
  { key: "BALLROOM",      lat: 36.1260, lng: -115.1694, label: "Ballroom",      tooltipDir: "top"   },
];

const VEX_OUTLINE = [
  [36.1244, -115.1725],
  [36.1244, -115.1680],
  [36.1267, -115.1680],
  [36.1267, -115.1725],
];

const VEX_ZONES = [
  { key: "PREFUNCTION", color: "#10B981",
    poly: [[36.1244,-115.1725],[36.1244,-115.1712],[36.1267,-115.1712],[36.1267,-115.1725]] },
  { key: "HALLS",       color: "#F59E0B",
    poly: [[36.1244,-115.1712],[36.1244,-115.1686],[36.1253,-115.1686],[36.1253,-115.1712]] },
  { key: "BALLROOM",    color: "#7C3AED",
    poly: [[36.1253,-115.1700],[36.1253,-115.1686],[36.1267,-115.1686],[36.1267,-115.1700]] },
];

const VEX_ANCHOR_PROFILES = {
  MAIN_ENTRANCE: { capacity: 200, occupied: 80,  friction: 0.40, irop: "low",    wait: 2,  services: "Registration · Badge Pickup · Info", congestion: "#10B981" },
  HALL_A:        { capacity: 500, occupied: 320, friction: 0.64, irop: "medium", wait: 5,  services: "Exhibitor Hall A · North Wing",       congestion: "#F59E0B" },
  HALL_B:        { capacity: 500, occupied: 410, friction: 0.82, irop: "high",   wait: 10, services: "Exhibitor Hall B · Center Stage",     congestion: "#EF4444" },
  HALL_C:        { capacity: 500, occupied: 290, friction: 0.58, irop: "medium", wait: 4,  services: "Exhibitor Hall C · Demo Floor",       congestion: "#F59E0B" },
  HALL_D:        { capacity: 500, occupied: 190, friction: 0.38, irop: "low",    wait: 0,  services: "Exhibitor Hall D · South Wing",       congestion: "#10B981" },
  BALLROOM:      { capacity: 800, occupied: 600, friction: 0.75, irop: "medium", wait: 8,  services: "Venetian Ballroom · Keynote · Dinner", congestion: "#F59E0B" },
};

// ─ Las Vegas Convention Center (LVCC) ─────────────────────────────────────────
const LVCC_ANCHORS = [
  { key: "WEST_HALL",    lat: 36.1357, lng: -115.1550, label: "West Hall",    tooltipDir: "left"  },
  { key: "NORTH_HALL",   lat: 36.1368, lng: -115.1530, label: "North Hall",   tooltipDir: "top"   },
  { key: "CENTRAL_HALL", lat: 36.1357, lng: -115.1523, label: "Central Hall", tooltipDir: "right" },
  { key: "CONNECTOR",    lat: 36.1350, lng: -115.1513, label: "Connector",    tooltipDir: "right" },
  { key: "SOUTH_HALL",   lat: 36.1344, lng: -115.1505, label: "South Hall",   tooltipDir: "right" },
];

const LVCC_OUTLINE = [
  [36.1338, -115.1558],
  [36.1338, -115.1495],
  [36.1380, -115.1495],
  [36.1380, -115.1558],
];

const LVCC_ZONES = [
  { key: "WEST",    color: "#10B981",
    poly: [[36.1338,-115.1558],[36.1338,-115.1532],[36.1380,-115.1532],[36.1380,-115.1558]] },
  { key: "NORTH",   color: "#F59E0B",
    poly: [[36.1355,-115.1532],[36.1355,-115.1515],[36.1380,-115.1515],[36.1380,-115.1532]] },
  { key: "SOUTH",   color: "#3B82F6",
    poly: [[36.1338,-115.1532],[36.1338,-115.1495],[36.1355,-115.1495],[36.1355,-115.1532]] },
];

const LVCC_ANCHOR_PROFILES = {
  WEST_HALL:    { capacity: 1000, occupied: 650, friction: 0.65, irop: "medium", wait: 5,  services: "West Hall · Outdoor Lots · AV Truck Bay", congestion: "#F59E0B" },
  NORTH_HALL:   { capacity: 800,  occupied: 520, friction: 0.65, irop: "medium", wait: 4,  services: "North Hall · Upper Concourse",             congestion: "#F59E0B" },
  CENTRAL_HALL: { capacity: 1200, occupied: 950, friction: 0.79, irop: "high",   wait: 12, services: "Central Hall · Main Stage · Media Row",     congestion: "#EF4444" },
  CONNECTOR:    { capacity: 200,  occupied: 110, friction: 0.55, irop: "medium", wait: 3,  services: "Connector Walkway · Monorail Access",        congestion: "#F59E0B" },
  SOUTH_HALL:   { capacity: 900,  occupied: 480, friction: 0.53, irop: "low",    wait: 2,  services: "South Hall · Meeting Rooms · Business Hub",  congestion: "#10B981" },
};

// ── Venue floor-plan registry (used by dynamic legend) ───────────────────────
//
// Maps property_code → { label, anchors, profiles }
// PropertyMapView legend renders anchors[selectedProperty] when set.
const VENUE_FLOOR_PLANS = {
  JFK_T5: { label: "JFK T5 Anchor Path",         anchors: null,         profiles: null         },
  MGM:    { label: "MGM Grand — Walking Path",    anchors: MGM_ANCHORS,  profiles: MGM_ANCHOR_PROFILES  },
  VEX:    { label: "Venetian Expo — Hall Path",   anchors: VEX_ANCHORS,  profiles: VEX_ANCHOR_PROFILES  },
  LVCC:   { label: "LVCC — Convention Path",      anchors: LVCC_ANCHORS, profiles: LVCC_ANCHOR_PROFILES },
};

// JFK T5 interior — zoomed to see the building at street level
// Center is near the midpoint of the terminal; zoom 18 puts individual buildings
// and roadways clearly on screen so markers visibly sit *on* the terminal.
const CENTER = [40.6432, -73.7858];
const ZOOM   = 18;

// JFK T5 anchor waypoints — updated to overlay the actual terminal sections.
//
// T5 (JetBlue) runs NE–SW across the airfield east side of JFK.
// Walking path: roadway curbside → ticket hall → security → landside lounge
//               → gate hold (B-gates concourse) → jetbridge airside.
//
// Coordinates verified against OSM / satellite at zoom 18-19:
//   Curbside drop-off road (western face)  ~(40.6415, -73.7885)
//   Ticket counter / bag drop hall        ~(40.6421, -73.7874)
//   TSA checkpoint (pre-security)         ~(40.6428, -73.7862)
//   JetBlue Mint Lounge / airside hall    ~(40.6434, -73.7850)
//   Gate B-concourse hold area            ~(40.6440, -73.7840)
//   Active jetbridge (B6–B38 end)         ~(40.6445, -73.7829)
const JFK_T5_ANCHORS = [
  { key: "CURBSIDE",    lat: 40.6415, lng: -73.7885, label: "Curbside",  tooltipDir: "left"  },
  { key: "BAG_DROP",    lat: 40.6421, lng: -73.7874, label: "Bag Drop",  tooltipDir: "left"  },
  { key: "TSA_LANE",    lat: 40.6428, lng: -73.7862, label: "TSA",       tooltipDir: "right" },
  { key: "LOUNGE_SEAT", lat: 40.6434, lng: -73.7850, label: "Lounge",    tooltipDir: "right" },
  { key: "GATE_HOLD",   lat: 40.6440, lng: -73.7840, label: "Gate Hold", tooltipDir: "right" },
  { key: "JETBRIDGE",   lat: 40.6445, lng: -73.7829, label: "Jetbridge", tooltipDir: "right" },
];

// ── JFK T5 building floor plan ─────────────────────────────────────────────
//
// Approximate terminal footprint derived from the anchor path centerline
// (CURBSIDE→JETBRIDGE bearing ≈ 62° from north) with ±40m perpendicular walls.
// Rendered as a Leaflet polygon overlay so the property view reads clearly as
// "inside a terminal building" rather than bare OSM tiles.
//
// Real Phase 2A: replace with GeoJSON from DigitalTwinSite.scene_bounds +
// floor_plan polygons loaded from the digital_twin_site prop.

// Terminal outline — rotated rectangle enclosing the full anchor path.
// Updated to match the revised anchor coordinates (CURBSIDE ~-73.7885 →
// JETBRIDGE ~-73.7829).  Each wall is ~40m perpendicular to the NE-SW axis.
const JFK_T5_OUTLINE = [
  [40.64108, -73.78895],  // SW corner, curbside end
  [40.64418, -73.78338],  // SW corner, jetbridge end
  [40.64482, -73.78381],  // NE corner, jetbridge end
  [40.64172, -73.78938],  // NE corner, curbside end
];

// Functional zone rectangles — one per anchor, updated to match revised coords.
// Each zone is ~60m along-axis × 45m across-axis.
const JFK_T5_ZONES = [
  { key: "CURBSIDE",    color: "#10B981",
    poly: [[40.64108,-73.78895],[40.64160,-73.78797],[40.64165,-73.78800],[40.64113,-73.78898]] },
  { key: "BAG_DROP",    color: "#F59E0B",
    poly: [[40.64158,-73.78800],[40.64210,-73.78702],[40.64215,-73.78705],[40.64163,-73.78803]] },
  { key: "TSA_LANE",    color: "#EF4444",
    poly: [[40.64228,-73.78675],[40.64280,-73.78577],[40.64286,-73.78580],[40.64233,-73.78678]] },
  { key: "LOUNGE_SEAT", color: "#10B981",
    poly: [[40.64288,-73.78555],[40.64340,-73.78457],[40.64346,-73.78460],[40.64294,-73.78558]] },
  { key: "GATE_HOLD",   color: "#F59E0B",
    poly: [[40.64348,-73.78455],[40.64400,-73.78357],[40.64406,-73.78360],[40.64353,-73.78458]] },
  { key: "JETBRIDGE",   color: "#EF4444",
    poly: [[40.64398,-73.78348],[40.64450,-73.78250],[40.64456,-73.78253],[40.64403,-73.78351]] },
];

// Per-anchor demo capacity / friction / IROP data
const ANCHOR_PROFILES = {
  CURBSIDE:    { capacity: 40, occupied: 12, friction: 0.30, irop: "low",      wait: 2,  services: "Rideshare · Taxi · Car Service",    congestion: "#10B981" },
  BAG_DROP:    { capacity: 25, occupied: 17, friction: 0.62, irop: "medium",   wait: 9,  services: "Checked Bags · Oversized",           congestion: "#F59E0B" },
  TSA_LANE:    { capacity: 15, occupied: 14, friction: 0.87, irop: "high",     wait: 24, services: "PreCheck · Standard Security",       congestion: "#EF4444" },
  LOUNGE_SEAT: { capacity: 60, occupied: 22, friction: 0.22, irop: "low",      wait: 0,  services: "Food & Beverage · Club Lounge",      congestion: "#10B981" },
  GATE_HOLD:   { capacity: 80, occupied: 38, friction: 0.48, irop: "medium",   wait: 5,  services: "Gate B6 · Boarding Hold Area",       congestion: "#F59E0B" },
  JETBRIDGE:   { capacity: 10, occupied: 9,  friction: 0.91, irop: "critical", wait: 0,  services: "Active Boarding · Wheelchair Assist", congestion: "#EF4444" },
};

// ── Helpers ────────────────────────────────────────────────────────────────

function anchorPopupHTML(anchor) {
  const p         = ANCHOR_PROFILES[anchor.key] || {};
  const pct       = p.capacity ? Math.round((p.occupied / p.capacity) * 100) : 0;
  const iropColor = { low: "#10B981", medium: "#F59E0B", high: "#EF4444", critical: "#7C3AED" }[p.irop] || "#6B7280";

  return `
    <div style="font-family:system-ui,sans-serif;min-width:210px;font-size:12px;color:#E0E0F0;">
      <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
        <div style="width:10px;height:10px;border-radius:50%;background:${p.congestion || "#6B7280"};flex-shrink:0;"></div>
        <strong style="font-size:13px;color:#E0E0F0;">${anchor.label}</strong>
        <span style="margin-left:auto;font-size:10px;padding:1px 6px;border-radius:8px;
                     background:${iropColor}33;color:${iropColor};border:1px solid ${iropColor}66;font-weight:600;">
          IROP: ${p.irop || "—"}
        </span>
      </div>
      <div style="background:#161F35;border:1px solid #1E2D52;border-radius:6px;padding:6px 8px;margin-bottom:6px;">
        <div style="display:flex;justify-content:space-between;color:#9CA3AF;margin-bottom:3px;font-size:11px;">
          <span>Occupancy</span>
          <span style="font-weight:700;color:#E0E0F0;">${p.occupied} / ${p.capacity} (${pct}%)</span>
        </div>
        <div style="background:#1E2D52;border-radius:4px;height:6px;overflow:hidden;">
          <div style="width:${pct}%;height:100%;background:${p.congestion || "#6B7280"};border-radius:4px;"></div>
        </div>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:11px;">
        <tr>
          <td style="padding:2px 4px;color:#6B7280;">Friction</td>
          <td style="padding:2px 4px;font-weight:600;color:${p.congestion || "#9CA3AF"};">${((p.friction || 0) * 100).toFixed(0)}%</td>
        </tr>
        <tr>
          <td style="padding:2px 4px;color:#6B7280;">Wait</td>
          <td style="padding:2px 4px;color:#D1D5DB;">${p.wait ? `~${p.wait} min` : "Immediate"}</td>
        </tr>
        <tr>
          <td style="padding:2px 4px;color:#6B7280;vertical-align:top;">Services</td>
          <td style="padding:2px 4px;color:#D1D5DB;">${p.services || "—"}</td>
        </tr>
      </table>
    </div>`;
}

// ── Phase D: venue floor plan renderer ────────────────────────────────────────
//
// Adds outline + zone + anchor-path layers for a given property code.
// Called once per code in the map init block — layers live at their geographic
// coordinates and only appear visually when the map is flyTo'd to that venue.
//
// Accepts a `profileMap` (e.g. MGM_ANCHOR_PROFILES) so anchor popups show
// capacity/friction data appropriate to each venue.

function renderVenueFloorPlan(L, map, code, outline, zones, anchors, profileMap) {
  // Building outline
  L.polygon(outline, {
    color:       "#3B5280",
    weight:      2,
    opacity:     0.8,
    fillColor:   "#0F1E3A",
    fillOpacity: 0.45,
  }).addTo(map)
    .bindTooltip(PROPERTY_REGISTRY[code]?.label || code, {
      sticky: true, className: "jq-anchor-label",
    });

  // Functional zone patches
  zones.forEach(({ color, poly }) => {
    if (poly.length < 3) return;
    L.polygon(poly, {
      color,
      weight:      1,
      opacity:     0.5,
      fillColor:   color,
      fillOpacity: 0.10,
      dashArray:   "2 3",
    }).addTo(map);
  });

  // Anchor path polyline
  L.polyline(anchors.map((a) => [a.lat, a.lng]), {
    color: "#4A6FA5", weight: 3, opacity: 0.8, dashArray: "6 4",
  }).addTo(map);

  // Anchor dots + labels + popups
  anchors.forEach((anchor) => {
    const profile  = profileMap?.[anchor.key] || {};
    const dotColor = profile.congestion || "#7C3AED";

    const dot = L.circleMarker([anchor.lat, anchor.lng], {
      radius: 7, color: dotColor, fillColor: "#0F1629", fillOpacity: 1, weight: 2.5,
    }).addTo(map);

    dot.bindTooltip(anchor.label, {
      permanent: true, direction: anchor.tooltipDir || "right",
      className: "jq-anchor-label",
      offset:    anchor.tooltipDir === "left" ? [-4, 0] : [4, 0],
    });

    // Reuse anchorPopupHTML logic inline (profile shape is identical)
    const pct       = profile.capacity ? Math.round((profile.occupied / profile.capacity) * 100) : 0;
    const iropColor = { low: "#10B981", medium: "#F59E0B", high: "#EF4444", critical: "#7C3AED" }[profile.irop] || "#6B7280";
    dot.bindPopup(`
      <div style="font-family:system-ui,sans-serif;min-width:210px;font-size:12px;color:#E0E0F0;">
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
          <div style="width:10px;height:10px;border-radius:50%;background:${profile.congestion || "#6B7280"};flex-shrink:0;"></div>
          <strong style="font-size:13px;color:#E0E0F0;">${anchor.label}</strong>
          <span style="margin-left:auto;font-size:10px;padding:1px 6px;border-radius:8px;
                       background:${iropColor}33;color:${iropColor};border:1px solid ${iropColor}66;font-weight:600;">
            IROP: ${profile.irop || "—"}
          </span>
        </div>
        <div style="background:#161F35;border:1px solid #1E2D52;border-radius:6px;padding:6px 8px;margin-bottom:6px;">
          <div style="display:flex;justify-content:space-between;color:#9CA3AF;margin-bottom:3px;font-size:11px;">
            <span>Occupancy</span>
            <span style="font-weight:700;color:#E0E0F0;">${profile.occupied ?? "—"} / ${profile.capacity ?? "—"} (${pct}%)</span>
          </div>
          <div style="background:#1E2D52;border-radius:4px;height:6px;overflow:hidden;">
            <div style="width:${pct}%;height:100%;background:${profile.congestion || "#6B7280"};border-radius:4px;"></div>
          </div>
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:11px;">
          <tr><td style="padding:2px 4px;color:#6B7280;">Friction</td>
              <td style="padding:2px 4px;font-weight:600;color:${profile.congestion || "#9CA3AF"};">${((profile.friction || 0) * 100).toFixed(0)}%</td></tr>
          <tr><td style="padding:2px 4px;color:#6B7280;">Wait</td>
              <td style="padding:2px 4px;color:#D1D5DB;">${profile.wait ? `~${profile.wait} min` : "Immediate"}</td></tr>
          <tr><td style="padding:2px 4px;color:#6B7280;vertical-align:top;">Services</td>
              <td style="padding:2px 4px;color:#D1D5DB;">${profile.services || "—"}</td></tr>
        </table>
      </div>`, { maxWidth: 260, className: "jq-anchor-popup" });
  });
}

// ── AR Node marker helpers ─────────────────────────────────────────────────

function buildArNodeIcon(serviceLabel, unpositioned) {
  const L           = window.L;
  const borderColor = unpositioned ? "#F59E0B" : "#7C3AED";
  const bgColor     = unpositioned ? "#1A1000" : "#12082A";
  const html = `
    <div style="
      width:34px; height:34px; border-radius:5px;
      background:${bgColor}; border:2px solid ${borderColor};
      display:flex; flex-direction:column; align-items:center; justify-content:center;
      box-shadow: 0 3px 10px rgba(0,0,0,0.7);
      font-size:14px; color:${borderColor}; line-height:1; position:relative;
      font-weight:700;
    ">
      ⬡
      ${unpositioned
        ? `<div style="position:absolute;top:-6px;right:-6px;background:#F59E0B;
               color:#1A0800;border-radius:50%;width:14px;height:14px;font-size:9px;
               display:flex;align-items:center;justify-content:center;font-weight:800;
               box-shadow:0 1px 4px rgba(0,0,0,0.5);">!</div>`
        : ""}
    </div>`;
  return L.divIcon({ html, className: "", iconSize: [34, 34], iconAnchor: [17, 34] });
}

function buildArNodePopupHTML(node) {
  const unpositioned = !node.positioned;
  const priceUsd     = node.price_cents ? `$${(node.price_cents / 100).toFixed(2)}` : "Free";
  const capUsed      = node.capacity - (node.availability ?? node.capacity);
  const capUtil      = node.capacity > 0 ? Math.round((capUsed / node.capacity) * 100) : 0;
  const statusBg     = node.status === "active" ? "#064E3B" : "#78350F";
  const statusFg     = node.status === "active" ? "#10B981" : "#F59E0B";
  return `
    <div style="font-family:system-ui,sans-serif;min-width:220px;font-size:12px;color:#E0E0F0;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
        <span style="font-size:20px;color:#818CF8;line-height:1;">⬡</span>
        <div>
          <div style="font-size:13px;font-weight:700;color:#E0E0F0;">${node.service_label}</div>
          <div style="font-size:10px;color:#6B7280;">AR Node #${node.id} · ${node.offer?.name || "—"}</div>
        </div>
        <div style="margin-left:auto;font-size:10px;padding:2px 7px;border-radius:10px;
             background:${statusBg};color:${statusFg};font-weight:700;border:1px solid ${statusFg}66;">
          ${node.status}
        </div>
      </div>

      ${unpositioned ? `
        <div style="background:#422006;border:1px solid #F59E0B;border-radius:5px;
             padding:5px 9px;margin-bottom:8px;font-size:11px;color:#FCD34D;display:flex;gap:6px;align-items:center;">
          <span style="font-size:14px;">⚠</span>
          <span>Drag this marker to its final location to save position.</span>
        </div>` : ""}

      <table style="width:100%;border-collapse:collapse;font-size:11px;">
        <tr>
          <td style="padding:3px 4px;color:#6B7280;">Price</td>
          <td style="padding:3px 4px;font-weight:700;color:#A5B4FC;">${priceUsd}</td>
        </tr>
        <tr>
          <td style="padding:3px 4px;color:#6B7280;">Capacity</td>
          <td style="padding:3px 4px;color:#D1D5DB;">${node.availability ?? "—"} / ${node.capacity} available (${capUtil}% used)</td>
        </tr>
        <tr>
          <td style="padding:3px 4px;color:#6B7280;">Published</td>
          <td style="padding:3px 4px;color:#D1D5DB;">${node.published_at ? new Date(node.published_at).toLocaleString() : "—"}</td>
        </tr>
      </table>

      <div style="display:flex;gap:8px;margin-top:10px;flex-wrap:wrap;">
        <a href="/admin/ar_nodes"
           style="flex:1;text-align:center;padding:5px 10px;border-radius:6px;
                  background:rgba(99,102,241,0.15);border:1px solid #6366F1;
                  color:#A5B4FC;text-decoration:none;font-size:11px;font-weight:600;">
          🗂 Manage All
        </a>
        <button
          data-ar-node-delete="${node.id}"
          style="flex:1;padding:5px 10px;border-radius:6px;cursor:pointer;
                 background:rgba(239,68,68,0.1);border:1px solid #EF4444;
                 color:#FCA5A5;font-size:11px;font-weight:600;">
          🗑 Delete
        </button>
      </div>
    </div>`;
}

/**
 * normalizeArNodePosition — ensures every AR node has a valid `position` object
 * with numeric lat/lng, regardless of whether it arrived via:
 *   (a) Inertia initial props (controller serializes position: { lat, lng })
 *   (b) ActionCable AR_NODE_DEPLOYED (may arrive as { latitude, longitude } only)
 *   (c) handleArNodePositioned drag update (already sets position: { lat, lng })
 *
 * Nodes with no recoverable coordinates are filtered out (returns null).
 * Fallback: JFK T5 centre — matches the controller's effective_centre default.
 */
const JFK_T5_FALLBACK = { lat: 40.6432, lng: -73.7858 };

function normalizeArNode(n) {
  // Prefer the position sub-object if it has valid numbers
  const posLat = Number(n.position?.lat);
  const posLng = Number(n.position?.lng);
  if (isFinite(posLat) && isFinite(posLng) && (posLat !== 0 || posLng !== 0)) {
    return { ...n, position: { lat: posLat, lng: posLng } };
  }
  // Fall back to top-level latitude/longitude fields (ActionCable format)
  const rawLat = Number(n.latitude);
  const rawLng = Number(n.longitude);
  if (isFinite(rawLat) && isFinite(rawLng) && (rawLat !== 0 || rawLng !== 0)) {
    return { ...n, position: { lat: rawLat, lng: rawLng } };
  }
  // Fall back to JFK T5 centre (matches controller default for unpositioned nodes)
  return { ...n, position: { ...JFK_T5_FALLBACK } };
}

/**
 * applyPositionJitter — if multiple AR nodes share the same resolved lat/lng
 * (happens when several unpositioned nodes have the same anchor category or
 * both fall through to the JFK T5 centre), spread them in a small circle so
 * they are individually draggable instead of all stacking on the same pixel.
 *
 * Radius ~35m at this zoom level (0.0003° ≈ 33m).
 * Nodes with an explicit confirmed position (positioned: true) are never moved.
 * Input nodes MUST be normalized (normalizeArNode) before calling this.
 */
function applyPositionJitter(nodes) {
  const RADIUS = 0.0003;
  const groups = {};
  nodes.forEach((n) => {
    const key = `${n.position.lat.toFixed(4)},${n.position.lng.toFixed(4)}`;
    (groups[key] = groups[key] || []).push(n);
  });

  return nodes.map((n) => {
    if (n.positioned) return n;           // confirmed positions are never jittered
    const key   = `${n.position.lat.toFixed(4)},${n.position.lng.toFixed(4)}`;
    const group = groups[key];
    if (group.length <= 1) return n;      // sole occupant — no jitter needed
    const idx   = group.indexOf(n);
    const angle = (2 * Math.PI * idx) / group.length;
    return {
      ...n,
      position: {
        lat: n.position.lat + RADIUS * Math.cos(angle),
        lng: n.position.lng + RADIUS * Math.sin(angle),
      },
    };
  });
}

// ── Component ──────────────────────────────────────────────────────────────

export default function PropertyMapView({
  sessions,
  arNodes = [],
  onArNodePositioned,
  queueRegistryRef,
  isActive,
  // Property navigation props (Step 7):
  //   selectedProperty — property_code string from PROPERTY_REGISTRY (e.g. "JFK_T5")
  //   onPropertySelect — callback(code|null) fired by breadcrumb × to clear focus
  selectedProperty,
  onPropertySelect,
}) {
  const mapElRef         = useRef(null);
  const mapRef           = useRef(null);
  const anchorsRef       = useRef([]);
  const arNodeMarkersRef = useRef(new Map());  // arNode.id → Leaflet marker
  // FIX: track which node ids already have dragend attached so re-renders
  // caused by ActionCable session updates never add a second listener.
  const dragListenedRef  = useRef(new Set());

  // Recalculate map size whenever this tab is revealed
  useEffect(() => {
    if (isActive && mapRef.current) {
      setTimeout(() => mapRef.current?.invalidateSize(), 50);
    }
  }, [isActive]);

  // ── Fly to selected property ───────────────────────────────────────────
  // Fires when the user clicks a venue chip in CityMapView and switches to this tab.
  // The map is already initialised by the time selectedProperty becomes non-null
  // (map init runs on first mount; selectedProperty starts as null in Index.jsx).
  //
  // IMPORTANT: this effect fires for EVERY selectedProperty change, including
  // Cesium venues (arena/stadium) where Index.jsx renders CesiumPropertyView
  // instead — PropertyMapView's wrapper is display:none in that case.
  // A display:none container reports 0×0 to Leaflet's getSize(); flyTo's Van Wijk
  // animation formula divides by that → NaN propagates to every animation frame
  // → "Invalid LatLng object: (NaN, NaN)" on every requestAnimationFrame tick.
  // Guard: skip flyTo entirely for Cesium venues (their wrapper is display:none
  // when selected; a 0×0 Leaflet container causes NaN in Van Wijk zoom animation).
  // Keep in sync with CESIUM_VENUE_TYPES in CesiumPropertyView.jsx.
  const CESIUM_VENUE_TYPES = new Set(["arena", "stadium", "airport"]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedProperty) return;
    const prop = PROPERTY_REGISTRY[selectedProperty];
    if (!prop) return;

    // Skip: PropertyMapView's wrapper div is display:none for arena/stadium venues
    // (Index.jsx shows CesiumPropertyView instead). Calling flyTo on a 0×0 container
    // would divide by zero in Leaflet's Van Wijk animation → cascading NaN errors.
    if (CESIUM_VENUE_TYPES.has(prop.venueType)) return;

    // CRITICAL: the outer wrapper div switches from display:none → display:flex
    // in the SAME React commit that sets selectedProperty (user clicks a venue chip
    // from the City tab). The isActive effect schedules invalidateSize() via
    // setTimeout(50ms) — but this flyTo effect fires synchronously in the same pass,
    // before that timeout runs. Leaflet's flyTo uses map.getSize() in its Van Wijk
    // animation formula: if the container is still reporting 0×0 at call time,
    // the formula divides by zero → NaN propagates through every animation frame
    // → "Invalid LatLng object: (NaN, NaN)" on every requestAnimationFrame tick.
    //
    // Reading clientWidth/clientHeight on a newly-visible element forces a
    // synchronous browser layout reflow, so invalidateSize() here gives Leaflet
    // the correct container dimensions before flyTo does any math.
    map.invalidateSize();

    // Leaflet flyTo([lat, lng], zoom)
    map.flyTo([prop.lat, prop.lng], prop.zoom, { animate: true, duration: 0.8 });
  }, [selectedProperty]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Passenger session markers ──────────────────────────────────────────
  useEffect(() => {
    const L = window.L;
    if (!L || !mapElRef.current) return;

    if (!mapRef.current) {
      mapRef.current = L.map(mapElRef.current, { center: CENTER, zoom: ZOOM, zoomControl: true });
      L.tileLayer(TILE_URL, { attribution: TILE_ATTR, maxZoom: 20 }).addTo(mapRef.current);
      setTimeout(() => mapRef.current?.invalidateSize(), 50);

      // ── AR node popup delete button handler ───────────────────────────
      // The delete button inside buildArNodePopupHTML uses a data attribute so
      // we can identify it without relying on Leaflet's popup close timing.
      mapRef.current.on("popupopen", (e) => {
        const btn = e.popup.getElement()?.querySelector("[data-ar-node-delete]");
        if (!btn) return;
        const nodeId = btn.getAttribute("data-ar-node-delete");
        btn.addEventListener("click", () => {
          if (!window.confirm(`Delete sensePoint #${nodeId}? This cannot be undone.`)) return;
          const csrf = document.querySelector('meta[name="csrf-token"]')?.content || "";
          fetch(`/admin/ar_nodes/${nodeId}`, {
            method:  "DELETE",
            headers: { "X-CSRF-Token": csrf, Accept: "application/json" },
          })
            .then((r) => {
              if (!r.ok) throw new Error(`HTTP ${r.status}`);
              return r.json();
            })
            .then(() => {
              const id     = parseInt(nodeId, 10);
              const marker = arNodeMarkersRef.current.get(id);
              if (marker) {
                marker.closePopup();
                mapRef.current?.removeLayer(marker);
                arNodeMarkersRef.current.delete(id);
                dragListenedRef.current.delete(id);
              }
            })
            .catch((err) => window.alert(`Delete failed: ${err.message}`));
        });
      });

      // ── Floor plan: terminal building outline ──────────────────────────
      L.polygon(JFK_T5_OUTLINE, {
        color:       "#3B5280",
        weight:      2,
        opacity:     0.8,
        fillColor:   "#0F1E3A",
        fillOpacity: 0.45,
      }).addTo(mapRef.current)
        .bindTooltip("JFK Terminal 5 — JetBlue", {
          sticky: true, className: "jq-anchor-label",
        });

      // ── Floor plan: functional zone patches ────────────────────────────
      JFK_T5_ZONES.forEach(({ key, color, poly }) => {
        // Fix any truncated lat values (typo safety: ensure full 7-digit lats)
        const validPoly = poly.filter(([lat]) => lat > 40);
        if (validPoly.length < 3) return;
        L.polygon(validPoly, {
          color:       color,
          weight:      1,
          opacity:     0.5,
          fillColor:   color,
          fillOpacity: 0.10,
          dashArray:   "2 3",
        }).addTo(mapRef.current);
      });

      // Anchor path polyline (drawn on top of floor plan)
      L.polyline(JFK_T5_ANCHORS.map((a) => [a.lat, a.lng]), {
        color: "#4A6FA5", weight: 3, opacity: 0.8, dashArray: "6 4",
      }).addTo(mapRef.current);

      // ── Phase D: LV venue floor plans (added at their geographic coordinates) ──
      // These layers coexist with JFK T5 layers at their own lat/lng.
      // They become visible when the user clicks an LV venue chip and flyTo
      // moves the map to the venue.  No extra cost while viewing JFK T5.
      renderVenueFloorPlan(L, mapRef.current, "MGM",  MGM_OUTLINE,  MGM_ZONES,  MGM_ANCHORS,  MGM_ANCHOR_PROFILES);
      renderVenueFloorPlan(L, mapRef.current, "VEX",  VEX_OUTLINE,  VEX_ZONES,  VEX_ANCHORS,  VEX_ANCHOR_PROFILES);
      renderVenueFloorPlan(L, mapRef.current, "LVCC", LVCC_OUTLINE, LVCC_ZONES, LVCC_ANCHORS, LVCC_ANCHOR_PROFILES);

      // Anchor dots + labels
      anchorsRef.current = JFK_T5_ANCHORS.map((anchor) => {
        const profile  = ANCHOR_PROFILES[anchor.key] || {};
        const dotColor = profile.congestion || "#7C3AED";
        const dot = L.circleMarker([anchor.lat, anchor.lng], {
          radius: 7, color: dotColor, fillColor: "#0F1629", fillOpacity: 1, weight: 2.5,
        }).addTo(mapRef.current);
        dot.bindTooltip(anchor.label, {
          permanent: true, direction: anchor.tooltipDir, className: "jq-anchor-label",
          offset:    anchor.tooltipDir === "left" ? [-4, 0] : [4, 0],
        });
        dot.bindPopup(anchorPopupHTML(anchor), { maxWidth: 260, className: "jq-anchor-popup" });
        return dot;
      });
    }

    const map         = mapRef.current;
    const propSessions = sessions.filter((s) => s.scope === "property");

    propSessions.forEach((session) => {
      // Guard: session.position may have undefined/null lat/lng if ActionCable
      // delivered a broadcast before the first DB write completed.
      const pos = session.position;
      if (!pos || !isFinite(Number(pos.lat)) || !isFinite(Number(pos.lng))) return;

      const existing = queueRegistryRef.current.get(session.id);
      if (existing) {
        // updatePosition guards its own coords; fall back gracefully if it's
        // a CityMap entry ({ cityJq }) that somehow appears in propSessions.
        if (typeof existing.updatePosition === "function") {
          existing.updatePosition(pos, session.alert_color);
        }
      } else {
        const jq = new JourneyQueue(session);
        queueRegistryRef.current.set(session.id, jq);
        jq.addToMap(map);
      }
    });

    // Remove stale passenger markers
    queueRegistryRef.current.forEach((jq, id) => {
      if (jq.scope === "property" && !propSessions.find((s) => s.id === id)) {
        jq.removeFromMap();
        queueRegistryRef.current.delete(id);
      }
    });
  }, [sessions]);

  // ── AR Node sensePoint markers ─────────────────────────────────────────
  // Uses visibleArNodes (filtered by selectedProperty when set) rather than arNodes.
  // This keeps the map showing only nodes relevant to the focused property,
  // which matters most when switching from JFK T5 to an LV venue.
  useEffect(() => {
    const L   = window.L;
    const map = mapRef.current;
    if (!L || !map || !visibleArNodes) return;

    // Normalize positions — handles both controller format (position: {lat,lng})
    // and ActionCable AR_NODE_DEPLOYED format (latitude, longitude at top level).
    // Filters out completely unrecoverable nodes before Leaflet ever sees them.
    const normalizedNodes = visibleArNodes.map(normalizeArNode);

    // Apply jitter to prevent co-located nodes from stacking
    const jitteredNodes = applyPositionJitter(normalizedNodes);
    const seenIds = new Set();

    jitteredNodes.forEach((node) => {
      seenIds.add(node.id);
      const existing = arNodeMarkersRef.current.get(node.id);

      if (existing) {
        // Refresh icon and popup on every render (positioned status may have changed)
        existing.setIcon(buildArNodeIcon(node.service_label, !node.positioned));
        existing.setPopupContent(buildArNodePopupHTML(node));
        // Update position if the source data changed (e.g. after a successful PATCH)
        const ll = existing.getLatLng();
        if (Math.abs(ll.lat - node.position.lat) > 0.00001 ||
            Math.abs(ll.lng - node.position.lng) > 0.00001) {
          existing.setLatLng([node.position.lat, node.position.lng]);
        }
        // Attach dragend only if not already done (FIX: deduplication guard)
        if (!node.positioned && !dragListenedRef.current.has(node.id)) {
          attachDragHandler(existing, node);
        }
        return;
      }

      // ── New marker ──────────────────────────────────────────────────────
      const marker = L.marker([node.position.lat, node.position.lng], {
        icon:         buildArNodeIcon(node.service_label, !node.positioned),
        draggable:    !node.positioned,
        title:        node.service_label,
        zIndexOffset: 500,
        opacity:      0,
      }).addTo(map);

      marker.bindPopup(buildArNodePopupHTML(node), { maxWidth: 280 });
      marker.bindTooltip(node.service_label, { direction: "top", offset: [0, -36], className: "jq-anchor-label" });

      arNodeMarkersRef.current.set(node.id, marker);

      // Drop-in animation
      const el = marker.getElement();
      if (el) {
        el.style.transition = "none";
        el.style.transform  = "translateY(-40px)";
        el.style.opacity    = "0";
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            el.style.transition = "transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.4s ease";
            el.style.transform  = "translateY(0)";
            el.style.opacity    = "1";
            marker.setOpacity(1);
          });
        });
      } else {
        marker.setOpacity(1);
      }

      if (!node.positioned) {
        attachDragHandler(marker, node);
      }
    });

    // Remove markers for retired or missing AR nodes
    arNodeMarkersRef.current.forEach((marker, id) => {
      if (!seenIds.has(id)) {
        map.removeLayer(marker);
        arNodeMarkersRef.current.delete(id);
        dragListenedRef.current.delete(id);
      }
    });
  }, [arNodes, selectedProperty]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Drag-to-place handler ──────────────────────────────────────────────
  // Extracted so the deduplication guard can reference it by stable identity.
  // Uses the admin PATCH /admin/ar_nodes/:id/position (cookie/CSRF auth) —
  // NOT the API route /api/v1/unified_api/ar_nodes/:id which requires a Bearer token.
  function attachDragHandler(marker, node) {
    dragListenedRef.current.add(node.id);

    marker.on("dragend", function (e) {
      const { lat, lng } = e.target.getLatLng();

      const csrfToken =
        document.querySelector('meta[name="csrf-token"]')?.content || "";

      fetch(`/admin/ar_nodes/${node.id}/position`, {
        method:  "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ latitude: lat, longitude: lng }),
      })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then(() => {
          // Update icon to "positioned" (remove warning badge)
          marker.setIcon(buildArNodeIcon(node.service_label, false));
          marker.setPopupContent(buildArNodePopupHTML({ ...node, positioned: true }));
          marker.dragging?.disable();
          // Notify parent so React state reflects the new position
          if (onArNodePositioned) onArNodePositioned(node.id, lat, lng);
        })
        .catch((err) => {
          console.error("[PropertyMapView] position save failed:", err);
          // Revert marker to original position on failure
          marker.setLatLng([node.position.lat, node.position.lng]);
        });
    });
  }

  // ── Render ─────────────────────────────────────────────────────────────
  //
  // When a property is focused, filter sessions and AR nodes to the bbox / property_code.
  // This prevents cross-property noise and keeps the legend count accurate.
  const propSessions = sessions.filter((s) => {
    if (s.scope !== "property") return false;
    return inPropertyBbox(selectedProperty, s.position);
  });

  // AR nodes: show nodes matching the focused property_code, or all when no focus.
  const visibleArNodes = selectedProperty
    ? arNodes.filter((n) => !n.property_code || n.property_code === selectedProperty)
    : arNodes;

  const unplacedNodes = visibleArNodes.filter((n) => !n.positioned);

  const focusedProp = selectedProperty ? PROPERTY_REGISTRY[selectedProperty] : null;

  // Dynamic legend: show anchor list for the focused property when a floor plan
  // exists for it, or fall back to JFK T5 (the default starting view).
  const venuePlan       = VENUE_FLOOR_PLANS[selectedProperty] || null;
  const legendAnchors   = venuePlan?.anchors || JFK_T5_ANCHORS;
  const legendLabel     = venuePlan?.label   || "JFK T5 Anchor Path";

  return (
    <div style={{ position: "relative", flex: 1 }}>
      {/* ── Property breadcrumb chip ────────────────────────────────────── */}
      {focusedProp && (
        <div style={{
          position:   "absolute",
          top:        12,
          left:       12,
          zIndex:     900,
          display:    "flex",
          alignItems: "center",
          gap:        6,
          background: "rgba(7,9,15,0.92)",
          border:     "1px solid #7C3AED",
          borderRadius: 8,
          padding:    "5px 10px 5px 10px",
          fontSize:   11,
          fontWeight: 700,
          color:      "#C4B5FD",
          pointerEvents: "auto",
        }}>
          <span style={{ fontSize: 14 }}>{focusedProp.emoji || "📍"}</span>
          <span>{focusedProp.label}</span>
          {onPropertySelect && (
            <button
              onClick={() => onPropertySelect(null)}
              title="Clear property focus — show all"
              style={{
                background:   "none",
                border:       "none",
                color:        "#6B7280",
                fontSize:     14,
                cursor:       "pointer",
                padding:      "0 0 0 4px",
                lineHeight:   1,
                fontWeight:   400,
                flexShrink:   0,
              }}
            >×</button>
          )}
        </div>
      )}

      {/* Legend */}
      <div style={{
        position: "absolute", top: 12, right: 12, zIndex: 800,
        background: "rgba(15,22,41,0.92)", borderRadius: 8,
        border: "1px solid #1E2D52", padding: "8px 12px",
        fontSize: 11, color: "#9CA3AF",
      }}>
        <div style={{ fontWeight: 700, marginBottom: 6, color: "#E0E0F0" }}>
          🚶 Property — Indoor
        </div>
        <div style={{ color: "#7C3AED", marginBottom: 4 }}>{legendLabel}</div>
        {legendAnchors.map((a, i) => (
          <div key={a.key} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 2 }}>
            <span style={{ color: "#4A5568", fontSize: 10 }}>{i + 1}.</span>
            <span>{a.label}</span>
          </div>
        ))}
        {(propSessions.length > 0 || visibleArNodes.length > 0) && (
          <div style={{ marginTop: 8, paddingTop: 6, borderTop: "1px solid #1E2D52" }}>
            {propSessions.length > 0 && (
              <div style={{ color: "#E0E0F0", fontWeight: 700, marginBottom: 3 }}>
                {propSessions.length} active {propSessions.length === 1 ? "session" : "sessions"}
              </div>
            )}
            {visibleArNodes.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 4, color: "#C4B5FD", fontWeight: 700 }}>
                <span>⬡</span>
                {visibleArNodes.length} AR node{visibleArNodes.length !== 1 ? "s" : ""}
                {unplacedNodes.length > 0 && (
                  <span style={{ color: "#F59E0B", fontSize: 10 }}>
                    ({unplacedNodes.length} need placement)
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {propSessions.length === 0 && visibleArNodes.length === 0 && <EmptyState />}

      <div ref={mapElRef} style={{ width: "100%", height: "100%", minHeight: 480 }} />
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{
      position: "absolute", inset: 0, zIndex: 900,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      background: "rgba(7,9,15,0.7)", borderRadius: 8,
      color: "#6B7280", pointerEvents: "none",
    }}>
      <div style={{ fontSize: 40, marginBottom: 8 }}>🚶</div>
      <div style={{ fontSize: 13, fontWeight: 600 }}>No active property sessions</div>
      <div style={{ fontSize: 11, marginTop: 4 }}>
        Seed demo: <code style={{ color: "#9CA3AF" }}>rails queue_map_monitor:seed_demo</code>
      </div>
    </div>
  );
}
